import { useIsOrganization } from '@zelos/shared/lib/sign.service';
import { useCallback, useMemo } from 'react';

export type GnbItem = {
  title: string,
  path?: string,
  newTab?: boolean,
  children?: GnbItem[],
};

type GnbBase = {
  title: string,
  path?: string,
  newTab?: boolean,
  children?: GnbBase[],
  notInProduction?: boolean,
  notInOrganization?: boolean,
  organizationOnly?: boolean,
  organizationTitle?: string,
};

const isProduction = process.env.IS_PRODUCTION;

const gnbBase: GnbBase[] = [
  {
    title: '시험 접수',
    children: [
      {
        title: '시험 일정 및 접수',
        path: '/tryouts',
        organizationTitle: '단체 시험 일정 및 접수',
      },
      {
        title: '접수 내역',
        path: '/user-registrations',
        organizationTitle: '단체 접수 내역',
      },
      {
        title: '응시자 목록',
        path: '/applicants',
        organizationTitle: '단체 응시자 목록',
        organizationOnly: true,
      },
      {
        title: '사전테스트',
        path: '/tryouts/pre-test',
        notInOrganization: true,
      },
      {
        title: '시험 응시',
        path: '/tryouts/test',
        notInOrganization: true,
      },
    ],
  },
  {
    title: '성적 확인',
    children: [
      {
        title: '성적확인 및 성적표/인증서 발급',
        path: '/result/issuance',
        organizationTitle: '단체 성적 확인',
      },
      {
        title: '성적 진위여부 확인',
        path: '/result/verification',
      },
    ],
  },
  {
    title: '시험 소개',
    children: [
      {
        title: '시험 소개',
        path: '/about/main',
      },
      {
        title: 'PCCP',
        path: '/about/pccp',
      },
      {
        title: 'PCCE',
        path: '/about/pcce',
      },
      {
        title: 'PCSQL',
        path: '/about/pcsql',
      },
      {
        title: '샘플 문제',
        path: '/about/sample',
      },
    ],
  },
  {
    title: '수험자 가이드',
    children: [
      {
        title: '수험자 가이드',
        path: '/guide/main',
      },
      {
        title: '시험관리 규정',
        path: '/guide/regulation',
      },
      {
        title: '시험관리 FAQ',
        path: 'https://certi-guide.grepp.co/',
        newTab: true,
      },
    ],
  },
  {
    title: '활용 현황',
    children: [
      {
        title: '기업 활용 현황',
        path: '/partners/enterprise-use-cases',
      },
      {
        title: '대학교 활용 현황',
        path: '/partners/university-use-cases',
      },
      {
        title: '기업 활용 방안',
        path: '/partners/enterprise-use-plan',
      },
    ],
  },
  {
    title: '교육 정보',
    children: [
      {
        title: '교육 콘텐츠',
        path: '/education-info/education',
      },
      {
        title: '시험/교육센터',
        path: '/education-info/academy',
      },
    ],
  },
];

export const useGnbMenu = () => {
  const isOrganization = useIsOrganization();

  const genGnb = useCallback((gnbBase: GnbBase[]) =>
    gnbBase.reduce<GnbItem[]>((resultItem, {
      title,
      path,
      newTab,
      children,
      notInProduction,
      notInOrganization,
      organizationOnly,
      organizationTitle,
    }) => {
      if (notInProduction && isProduction) return resultItem;
      if (notInOrganization && isOrganization) return resultItem;
      if (organizationOnly && !isOrganization) return resultItem;

      resultItem.push({
        title: isOrganization ? organizationTitle || title : title,
        path,
        newTab,
        children: children && genGnb(children),
      });
      return resultItem;
    }, []), [isOrganization]);

  const gnbMenu = useMemo(() => genGnb(gnbBase), [genGnb]);

  return gnbMenu;
};
